import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { DateTime } from 'luxon';
import i18n from '@/i18n';
import { findAppLanguage, LANGUAGE_STORAGE_KEY, type AppLanguageCode } from '@/i18n/languages';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getFontFamily(fontFamily: string | null | undefined): string {
  const fontMap: Record<string, string> = {
    POPPINS: 'Poppins, sans-serif',
    GILL_SANS: 'Gill Sans, sans-serif',
    UBUNTU: 'Ubuntu, sans-serif',
    BITTER: 'Bitter, serif',
  };
  return fontFamily && fontMap[fontFamily] ? fontMap[fontFamily] : 'Poppins, sans-serif';
}

function normalizeLocale(lang: string): string {
  const known = findAppLanguage(lang);
  if (known) return known.locale;
  return lang.includes('-') ? lang : `${lang}-${lang.toUpperCase()}`;
}

const DATE_FORMAT_BY_LANGUAGE: Record<AppLanguageCode, string> = {
  fr: "cccc d LLLL yyyy 'à' HH'h'mm ZZZZ",
  en: "cccc, LLLL d, yyyy 'at' h:mm a ZZZZ",
  es: "cccc d 'de' LLLL 'de' yyyy, HH:mm ZZZZ",
  pt: "cccc, d 'de' LLLL 'de' yyyy 'às' HH:mm ZZZZ",
};

export function getLocale(): string {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return normalizeLocale(stored);
  } catch {}

  const i18nLang = i18n?.language;
  if (i18nLang) return normalizeLocale(i18nLang);

  if (typeof navigator === 'object' && navigator.language) {
    return normalizeLocale(navigator.language);
  }
  return 'fr-FR';
}

export function getFormattedDate(date: string, timezone?: string, lngOverride?: string): string {
  const locale = lngOverride ?? getLocale().split('-')[0].toLowerCase();
  const targetTimezone = timezone || 'utc';

  const dateTime = DateTime.fromISO(date, { zone: 'utc' })
    .setZone(targetTimezone)
    .setLocale(locale);

  const formatted = dateTime.toFormat(
    DATE_FORMAT_BY_LANGUAGE[findAppLanguage(locale)?.code ?? 'en'],
    {
      locale,
    },
  );
  return toCapitalizedFirstLetter(formatted);
}

export function toCapitalizedFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
